import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VISA_FAQ_CATEGORIES } from "@/content/visa-corpus/faq-defs";

export const Route = createFileRoute("/visa/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/visa-corpus");
    return { faqs: m.VISA_FAQ_TH };
  },
  component: VisaFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    const title = `วีซ่าและตรวจคนเข้าเมืองไทย — FAQ ${faqs.length} ข้อ อ้างอิงหน่วยงานราชการ`;
    const description =
      "รวมคำถามที่พบบ่อยเรื่องวีซ่าไทยและการตรวจคนเข้าเมือง — วีซ่ากับการอนุญาตให้อยู่ต่อ ตม.7 รายงานตัว 90 วัน TM30 เกษียณ ครอบครัว DTV LTR SMART และคนไทยยื่นวีซ่าไปต่างประเทศ อ้างอิง immigration.go.th และ thaievisa.go.th";
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/visa/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/visa/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "วีซ่าและตรวจคนเข้าเมือง FAQ", url: `${site}/visa/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/visa/faq")) },
      ],
    };
  },
});

function VisaFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <span>วีซ่าและตรวจคนเข้าเมือง FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          วีซ่าไทยและตรวจคนเข้าเมือง — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="text-muted-foreground mb-6">
          คำตอบทุกข้ออ้างอิงประกาศและคู่มือของหน่วยงานที่รับผิดชอบจริง ได้แก่ สำนักงานตรวจคนเข้าเมือง
          (immigration.go.th) ระบบ e-Visa กระทรวงการต่างประเทศ (thaievisa.go.th) กรมการจัดหางาน (doe.go.th)
          และสำนักงานคณะกรรมการส่งเสริมการลงทุน (boi.go.th)
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">ข้อควรทราบ:</strong> วีซ่าคือเอกสารที่อนุญาตให้เดินทางมาขอเข้าเมือง
          ไม่ใช่การรับประกันว่าจะได้รับอนุญาตให้เข้าประเทศ จำนวนวันที่อยู่ได้มาจากตราประทับของพนักงานเจ้าหน้าที่ที่ด่านตรวจ
          และการอนุมัติทุกกรณีเป็นดุลพินิจของเจ้าหน้าที่ — เราไม่รับประกันผลการพิจารณา
        </div>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/visa/faq" className="text-primary underline">
            Thailand visa &amp; immigration FAQ →
          </a>
        </div>

        <section aria-label="หมวดคำถามวีซ่า" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">เลือกดูตามหมวด</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {VISA_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/visa/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm hover:border-primary hover:shadow-sm transition"
              >
                <div className="font-semibold mb-1">{c.titleTh}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/recommend/visa-foreign">แนะนำบริการวีซ่าสำหรับชาวต่างชาติในไทย</a></li>
            <li><a className="text-primary underline" href="/recommend/visa-th">คนไทยยื่นวีซ่าไปต่างประเทศ</a></li>
            <li><a className="text-primary underline" href="/translation/faq">แปลเอกสารรับรองสำหรับยื่นวีซ่า</a></li>
            <li><a className="text-primary underline" href="/police-clearance">หนังสือรับรองความประพฤติ</a></li>
          </ul>
        </section>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ยังหาคำตอบไม่เจอ?</h2>
          <p className="text-sm text-muted-foreground mb-4">ปรึกษาทีมงาน — จ.-ส. 9:00-18:00</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ติดต่อทีมงาน →
          </a>
        </section>

        <FaqSourceNote topic="visa" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}